import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fmtTime, fromMinutes, toMinutes } from '../state/format';
import { QUARTERS } from '../../constants';

const e = React.createElement;

// Every control the form uses. Labels, captions and options are the payload's
// or the caller's; nothing here decides what a field means.

export const Field = ({
  label, value, onChange, onBlur, type = 'text', placeholder, caption, multiline,
  disabled, error,
}) =>
  e('div', { className: 'wd-fieldwrap' }, [
    label ? e('span', { className: 'wd-flabel', key: 'l' }, label) : null,
    e(multiline ? 'textarea' : 'input', {
      key: 'i',
      className: `wd-field${error ? ' is-error' : ''}`,
      // The visible label is a span beside the control, so the control carries
      // its own name for anything not reading the layout.
      'aria-label': label || undefined,
      value: value === undefined || value === null ? '' : value,
      type: multiline ? undefined : type,
      placeholder: placeholder || '',
      disabled: !!disabled,
      onChange: (ev) => onChange(ev.target.value),
      onBlur: onBlur || undefined,
    }),
    caption ? e('p', { className: 'wd-caption', key: 'c' }, caption) : null,
    error ? e('p', { className: 'wd-fielderror', key: 'e' }, error) : null,
  ]);

/**
 * A select with a search box, for a list too long to scroll: the couple
 * types a word and picks from what is left. The value is still one of the
 * options, so nothing new can be typed into it.
 */
export const SearchSelect = ({
  label, value, options, onChange, disabled, placeholder, caption, error,
  onCreate, createLabel, fallbackText, listNote, spares, spareNote,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState(0);
  const wrap = useRef(null);

  const chosen = options.find((o) => String(o.value) === String(value));
  // What the field reads when it is not being typed in: the venue they
  // picked, or the words they wrote where the list had none.
  const settled = chosen ? chosen.label : (fallbackText || '');
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().indexOf(q) >= 0);
  }, [options, query]);

  // Where the list has nothing for what they wrote, the nearest general
  // answers are offered rather than a dead end. They are rows of the list
  // like any other, so the arrow keys and Enter reach them too.
  const spare = (!shown.length && !onCreate) ? (spares || []) : [];
  const rows = shown.length ? shown : spare;

  // What they wrote, taken as written, where the list had nothing to offer
  // for it. Kept apart from closing the list so the two can happen at
  // different moments of the same press.
  const commit = () => {
    const typed = query.trim();
    setQuery('');
    // Nothing to take where they wrote nothing, or where what is in the
    // box is the answer it already held.
    if (!typed || typed === settled) return;
    if (onCreate && !shown.length) onCreate(typed);
  };

  const leave = () => {
    commit();
    setOpen(false);
  };

  /**
   * Pressing another control answers this one on the way past.
   *
   * The two halves sit on different events on purpose. What they typed is
   * taken on the press, because the control they are pressing runs its own
   * handler before any listener on the document: a venue written here and
   * left by pressing Save has to be in hand before Save reads the card, or
   * Save saves what was there before.
   *
   * The closing waits for the click. Closing on the press takes the target
   * out from under the pointer, and the browser then delivers no click at
   * all, which is a press that does nothing.
   */
  useEffect(() => {
    if (!open) return undefined;
    const outside = (ev) => wrap.current && !wrap.current.contains(ev.target);
    const take = (ev) => { if (outside(ev)) commit(); };
    const away = (ev) => { if (outside(ev)) leave(); };
    document.addEventListener('pointerdown', take);
    document.addEventListener('click', away);
    return () => {
      document.removeEventListener('pointerdown', take);
      document.removeEventListener('click', away);
    };
  });

  const pick = (option) => {
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  const onKey = (ev) => {
    if (ev.key === 'ArrowDown' || ev.key === 'ArrowUp') {
      ev.preventDefault();
      if (!open) return setOpen(true);
      const step = ev.key === 'ArrowDown' ? 1 : -1;
      return setAt((prev) => Math.min(Math.max(prev + step, 0), rows.length - 1));
    }
    if (ev.key === 'Enter' && open) {
      ev.preventDefault();
      // Enter on an empty list is the same answer as walking away from it.
      return rows[at] ? pick(rows[at]) : leave();
    }
    if (ev.key === 'Escape') {
      setQuery('');
      return setOpen(false);
    }
    return undefined;
  };

  return e('div', { className: 'wd-fieldwrap wd-search', ref: wrap }, [
    label ? e('span', { className: 'wd-flabel', key: 'l' }, label) : null,
    e('input', {
      key: 'i',
      className: `wd-field${error ? ' is-error' : ''}`,
      'aria-label': label || undefined,
      role: 'combobox',
      'aria-expanded': open,
      autoComplete: 'off',
      disabled: !!disabled,
      placeholder: placeholder || 'Search',
      value: open ? query : settled,
      // Opening it hands them what is already there to edit, rather than an
      // empty box: a field that clears itself on a tap leaves them unable
      // to see their own answer without leaving it again. Selected, so
      // typing replaces it the way an empty box would have.
      onFocus: (ev) => {
        if (!open) {
          setQuery(settled);
          setAt(0);
          ev.target.select();
        }
        setOpen(true);
      },
      onKeyDown: onKey,
      onChange: (ev) => {
        setQuery(ev.target.value);
        setAt(0);
        setOpen(true);
      },
    }),
    open
      ? e(
          'ul',
          { className: 'wd-searchlist', key: 'list', role: 'listbox' },
          [
            // Says what this list is before the first row, so a name they
            // cannot find reads as the wrong city rather than as a venue
            // we have never heard of.
            listNote
              ? e('li', { className: 'wd-searchnote', key: 'note' }, listNote)
              : null,
            spare.length
              ? e('li', { className: 'wd-searchnote', key: 'spare' },
                  spareNote || 'Not on the list. Pick the closest one.')
              : null,
          ].concat(
          rows.length
            ? rows.map((o, i) =>
                e(
                  'li',
                  {
                    key: String(o.value),
                    role: 'option',
                    'aria-selected': String(o.value) === String(value),
                    className: `wd-searchitem${i === at ? ' is-at' : ''}`,
                    // Fires before the input's blur, so the pick is not lost.
                    onMouseDown: (ev) => {
                      ev.preventDefault();
                      pick(o);
                    },
                    onMouseEnter: () => setAt(i),
                  },
                  // Where it is tells two venues of one name apart, so it
                  // rides beside the name here and stays out of the field.
                  [
                    e('span', { key: 'n' }, o.label),
                    o.hint
                      ? e('span', { className: 'wd-searchhint', key: 'h' },
                          o.hint)
                      : null,
                  ]
                )
              )
            : onCreate && query.trim()
              // Nothing matches, and what they wrote is the answer. Said
              // here so the empty list reads as accepted rather than as a
              // dead end, and taken when they leave the field.
              ? e(
                  'li',
                  { className: 'wd-searchempty', key: 'new' },
                  `${createLabel || 'We will use'} "${query.trim()}"`
                )
              : e('li', { className: 'wd-searchempty', key: 'none' }, 'Nothing matches that')
          )
        )
      : null,
    caption ? e('p', { className: 'wd-caption', key: 'c' }, caption) : null,
    error ? e('p', { className: 'wd-fielderror', key: 'e' }, error) : null,
  ]);
};

/**
 * A short list of named choices, drawn as the form's own control rather than
 * the browser's. A native select renders as the platform wants it to, which
 * on a phone is a wheel that hides what the other choices say, and these
 * choices are sentences the couple is meant to read side by side.
 */
export const PickSelect = ({ label, value, options, onChange, disabled,
  caption, error, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const chosen = options.find((o) => String(o.value) === String(value));

  // A click anywhere else closes it, which a blur alone would miss when the
  // click lands on another control. Taken on the click rather than the
  // press, so the control under the pointer still receives its own.
  useEffect(() => {
    if (!open) return undefined;
    const away = (ev) => {
      if (wrap.current && !wrap.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener('click', away);
    return () => document.removeEventListener('click', away);
  });

  return e('div', { className: 'wd-fieldwrap wd-search', ref: wrap }, [
    label ? e('span', { className: 'wd-flabel', key: 'l' }, label) : null,
    e(
      'button',
      {
        key: 'b',
        type: 'button',
        className: `wd-field wd-pick${error ? ' is-error' : ''}`,
        'aria-label': ariaLabel || label || undefined,
        'aria-haspopup': 'listbox',
        'aria-expanded': open,
        disabled: !!disabled,
        onClick: () => setOpen((was) => !was),
        onKeyDown: (ev) => {
          if (ev.key === 'Escape') setOpen(false);
        },
      },
      [
        e('span', { key: 't' }, chosen ? chosen.label : 'Select'),
        e('span', { className: 'wd-pickarrow', key: 'a', 'aria-hidden': 'true' }, '\u25be'),
      ]
    ),
    open
      ? e(
          'ul',
          { className: 'wd-searchlist', key: 'list', role: 'listbox' },
          options.map((o) =>
            e(
              'li',
              {
                key: String(o.value),
                role: 'option',
                'aria-selected': String(o.value) === String(value),
                className: `wd-searchitem${
                  String(o.value) === String(value) ? ' is-at' : ''}`,
                onMouseDown: (ev) => {
                  ev.preventDefault();
                  setOpen(false);
                  if (String(o.value) !== String(value)) onChange(o.value);
                },
              },
              o.label
            ))
        )
      : null,
    caption ? e('p', { className: 'wd-caption', key: 'c' }, caption) : null,
    error ? e('p', { className: 'wd-fielderror', key: 'e' }, error) : null,
  ]);
};

export const Select = ({ label, value, options, onChange, disabled, caption, error }) =>
  e('div', { className: 'wd-fieldwrap' }, [
    label ? e('span', { className: 'wd-flabel', key: 'l' }, label) : null,
    e(
      'select',
      {
        key: 's',
        className: `wd-field${error ? ' is-error' : ''}`,
        'aria-label': label || undefined,
        value: value === undefined || value === null ? '' : String(value),
        disabled: !!disabled,
        onChange: (ev) => onChange(ev.target.value),
      },
      options.map((o) => e('option', { key: String(o.value), value: String(o.value) }, o.label))
    ),
    caption ? e('p', { className: 'wd-caption', key: 'c' }, caption) : null,
    error ? e('p', { className: 'wd-fielderror', key: 'e' }, error) : null,
  ]);

/**
 * A time as a list rather than a free field, so the slots another event on the
 * same side already holds can be offered as unpickable. The server still
 * refuses an overlap: this only keeps the couple out of one.
 */
export const TimeSelect = ({ label, value, options, caption, disabled, onChange }) =>
  e('div', { className: 'wd-fieldwrap' }, [
    label ? e('span', { className: 'wd-flabel', key: 'l' }, label) : null,
    e(
      'select',
      {
        key: 's',
        className: 'wd-field',
        'aria-label': label || undefined,
        value: value || '',
        disabled: !!disabled,
        onChange: (ev) => onChange(ev.target.value),
      },
      options.map((o) =>
        e('option', { key: o.value, value: o.value, disabled: !!o.disabled }, o.label)
      )
    ),
    caption ? e('p', { className: 'wd-caption', key: 'c' }, caption) : null,
  ]);

/**
 * A time as one field, picked on two wheels: the hour, then the minute.
 *
 * Which times can be picked is not decided here: `options` is the list the
 * card has already worked out, so the package hours, the slots another event
 * holds and the couple shot's own minutes all arrive settled. Only those are
 * on the wheels. An hour with nothing free in it is not there at all, and the
 * minute wheel holds only the minutes free in the hour it sits under.
 *
 * Read on a 24 hour clock, so a day that runs past midnight never needs an
 * am or pm to say which side of it a time is on.
 *
 * The wheel is a draft. Nothing is sent until Done, because every time sent
 * is saved and checked, and a wheel passing through an hour on its way to
 * another would otherwise ask the server about each one.
 */
// One row of a wheel, used only to read a line-based wheel turn as pixels.
const WHEEL_ROW_PX = 40;

const Wheel = ({ label, items, at, onPick }) => {
  const list = useRef(null);
  const settle = useRef(null);
  // Only a scroll the couple made is a choice. The wheel moving itself to
  // the draft fires scroll events too, and reading those back picked
  // whatever row it happened to pass through.
  const touched = useRef(false);

  // The row nearest the middle, measured off the rows themselves so it
  // holds whatever height the page gives them.
  const centred = () => {
    const el = list.current;
    if (!el) return at;
    const middle = el.scrollTop + el.clientHeight / 2;
    let best = 0;
    let gap = Infinity;
    Array.prototype.forEach.call(el.children, (row, i) => {
      const d = Math.abs(row.offsetTop + row.offsetHeight / 2 - middle);
      if (d < gap) { gap = d; best = i; }
    });
    return best;
  };

  // Kept centred on the draft, including when the hour wheel moves and the
  // minute wheel gets a new list under it. Placed at once when the popup
  // opens, and eased after that so a step can be followed by eye.
  const placed = useRef(false);
  useEffect(() => {
    const el = list.current;
    const row = el && el.children[at];
    if (!row) return;
    const top = row.offsetTop + row.offsetHeight / 2 - el.clientHeight / 2;
    if (placed.current && el.scrollTo) {
      el.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollTop = top;
    }
    placed.current = true;
  }, [at, items]);

  useEffect(() => () => clearTimeout(settle.current), []);

  // A mouse wheel moves the page a few rows per notch, which on a wheel this
  // short skips straight past the time wanted. Taken over so one notch is
  // one row, and a trackpad's small movements add up to a row at a time.
  // Bound by hand because React's own wheel listener cannot stop the scroll.
  const latest = useRef({ at, items, onPick });
  latest.current = { at, items, onPick };
  useEffect(() => {
    const el = list.current;
    if (!el) return undefined;
    let pending = 0;
    const turn = (ev) => {
      ev.preventDefault();
      const rows = ev.deltaMode === 1 ? ev.deltaY * WHEEL_ROW_PX : ev.deltaY;
      pending += rows;
      if (Math.abs(pending) < WHEEL_ROW_PX) return;
      const by = pending > 0 ? 1 : -1;
      pending = 0;
      const now = latest.current;
      const next = Math.max(0, Math.min(now.items.length - 1, now.at + by));
      if (next !== now.at) now.onPick(next);
    };
    el.addEventListener('wheel', turn, { passive: false });
    return () => el.removeEventListener('wheel', turn);
  }, []);

  const onScroll = () => {
    if (!touched.current) return;
    clearTimeout(settle.current);
    // Read once the wheel stops, so a fling is one choice, not every row
    // it passed.
    settle.current = setTimeout(() => {
      touched.current = false;
      const i = centred();
      if (i !== at) onPick(i);
    }, 120);
  };

  const touch = () => { touched.current = true; };

  const step = (by) => {
    const next = Math.max(0, Math.min(items.length - 1, at + by));
    if (next !== at) onPick(next);
  };

  // Divs rather than a list: the quotation pages style every ul and li in
  // the app, padding included and some of it !important, and a wheel that
  // inherits that stops lining up with its band.
  return e('div', {
    ref: list,
    className: 'wd-wheel',
    role: 'listbox',
    tabIndex: 0,
    'aria-label': label,
    onScroll,
    onTouchStart: touch,
    onPointerDown: touch,
    onKeyDown: (ev) => {
      if (ev.key === 'ArrowUp') { ev.preventDefault(); step(-1); }
      if (ev.key === 'ArrowDown') { ev.preventDefault(); step(1); }
    },
  }, items.map((item, i) => e('div', {
    key: item.key,
    role: 'option',
    'aria-selected': i === at,
    className: `wd-wheelitem${i === at ? ' is-at' : ''}`,
    onClick: () => onPick(i),
  }, item.text)));
};

const pad2 = (n) => String(n).padStart(2, '0');

export const TimeParts = ({ label, value, options, caption, disabled, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);

  // In the order the card offered them, which is the order the day runs
  // rather than the order the clock counts.
  const allowed = useMemo(() => (options || [])
    .map((o) => (o.value ? toMinutes(o.value) : null))
    .filter((m) => m !== null && !Number.isNaN(m)), [options]);

  const hours = useMemo(() => {
    const out = [];
    allowed.forEach((m) => {
      const h = Math.floor(m / 60);
      if (out.indexOf(h) < 0) out.push(h);
    });
    return out;
  }, [allowed]);

  const freeIn = (h) => QUARTERS.filter((q) => allowed.indexOf(h * 60 + q) >= 0);

  // The couple reads a 12 hour clock, so the half of the day is picked
  // beside the hour rather than counted into it. Only the halves the day
  // actually offers are listed, in the order the day runs: an event that
  // starts in the evening offers pm before am.
  const halves = useMemo(() => {
    const out = [];
    hours.forEach((h) => {
      const part = h < 12 ? 'am' : 'pm';
      if (out.indexOf(part) < 0) out.push(part);
    });
    return out;
  }, [hours]);

  const current = toMinutes(value);
  const has = current !== null && !Number.isNaN(current);

  // Where the wheels open: on the saved time when it is still on offer,
  // otherwise on the first time that is.
  const startDraft = () => {
    if (has && allowed.indexOf(current) >= 0) {
      return { hour: Math.floor(current / 60), minute: current % 60 };
    }
    const first = hours[0];
    return first === undefined ? null : { hour: first, minute: freeIn(first)[0] };
  };
  const [draft, setDraft] = useState(null);

  // A click anywhere else is a dismissal, which a blur alone would miss when
  // the click lands on another control. Taken on the click rather than the
  // press: closing between the press and the release takes the target out
  // from under the pointer, and the browser then delivers no click at all.
  useEffect(() => {
    if (!open) return undefined;
    const away = (ev) => {
      if (wrap.current && !wrap.current.contains(ev.target)) setOpen(false);
    };
    const key = (ev) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('click', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('click', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const toggle = () => {
    if (!open) setDraft(startDraft());
    setOpen((was) => !was);
  };

  const draftHour = draft ? draft.hour : null;
  const draftHalf = draftHour === null ? null : (draftHour < 12 ? 'am' : 'pm');
  const minutes = useMemo(() => (draftHour === null ? []
    : QUARTERS.filter((q) => allowed.indexOf(draftHour * 60 + q) >= 0)),
  [draftHour, allowed]);
  // The hours of the half being shown. Both halves at once would list 11
  // twice with nothing to tell the morning one from the evening one.
  const shownHours = useMemo(() => (draftHalf === null ? []
    : hours.filter((h) => (h < 12 ? 'am' : 'pm') === draftHalf)),
  [hours, draftHalf]);
  // Built once per list rather than per render, so a card redrawing while a
  // wheel is turning does not snap it back.
  const hourItems = useMemo(
    () => shownHours.map((h) => ({ key: h, text: String(h % 12 === 0 ? 12 : h % 12) })),
    [shownHours]
  );
  const minuteItems = useMemo(() => minutes.map((q) => ({ key: q, text: pad2(q) })), [minutes]);
  const halfItems = useMemo(() => halves.map((p) => ({ key: p, text: p })), [halves]);

  // Moving to the other half of the day keeps the minute where it is free,
  // because the hour under it has changed and the minute was not what they
  // were turning.
  const pickHalf = (i) => {
    const part = halves[i];
    const hour = hours.find((h) => (h < 12 ? 'am' : 'pm') === part);
    if (hour === undefined) return;
    const free = freeIn(hour);
    setDraft((was) => ({
      hour,
      minute: was && free.indexOf(was.minute) >= 0 ? was.minute : free[0],
    }));
  };

  const pickHour = (i) => {
    const hour = shownHours[i];
    const free = freeIn(hour);
    // The same minute where it is free in the new hour, so moving an event
    // an hour on is one turn of one wheel.
    setDraft((was) => ({
      hour,
      minute: was && free.indexOf(was.minute) >= 0 ? was.minute : free[0],
    }));
  };

  const done = () => {
    if (draft && draft.minute !== undefined) {
      const picked = fromMinutes(draft.hour * 60 + draft.minute);
      if (picked !== value) onChange(picked);
    }
    setOpen(false);
  };

  return e('div', { className: 'wd-fieldwrap wd-picker', ref: wrap }, [
    label ? e('span', { className: 'wd-flabel', key: 'l' }, label) : null,
    // The popup hangs off the field itself rather than off the whole block,
    // so a caption under the field does not push it down.
    e('div', { className: 'wd-pickeranchor', key: 'a' }, [
      e('button', {
        key: 'b',
        type: 'button',
        className: 'wd-field wd-pickerbtn',
        disabled: !!disabled,
        'aria-haspopup': 'listbox',
        'aria-expanded': open,
        'aria-label': label || undefined,
        onClick: toggle,
      }, [
        e('span', { key: 't', className: has ? '' : 'wd-pickerempty' },
          has ? fmtTime(value) : 'Select a time'),
        e('span', { key: 'i', className: 'wd-pickercaret', 'aria-hidden': 'true' }),
    ]),
    open
      ? e('div', { className: 'wd-timepop', key: 'p' }, draft
        ? [
          e('div', { className: 'wd-wheels', key: 'w' }, [
            // One band behind both wheels, which is where the picked time
            // reads.
            e('div', { className: 'wd-wheelband', key: 'band', 'aria-hidden': 'true' }),
            e(Wheel, {
              key: 'h',
              label: 'Hour',
              items: hourItems,
              at: shownHours.indexOf(draft.hour),
              onPick: pickHour,
            }),
            e('span', { className: 'wd-wheelsep', key: 's', 'aria-hidden': 'true' }, ':'),
            e(Wheel, {
              key: 'm',
              label: 'Minute',
              items: minuteItems,
              at: minutes.indexOf(draft.minute),
              onPick: (i) => setDraft((was) => ({ ...was, minute: minutes[i] })),
            }),
            halves.length > 1
              ? e(Wheel, {
                key: 'p',
                label: 'Morning or evening',
                items: halfItems,
                at: halves.indexOf(draftHalf),
                onPick: pickHalf,
              })
              : e('span', { className: 'wd-wheelfixed', key: 'p' }, draftHalf),
          ]),
          e('button', {
            key: 'd',
            type: 'button',
            className: 'wd-wheeldone',
            onClick: done,
          }, 'Done'),
        ]
        : e('p', { className: 'wd-wheelnone' }, 'No time is free here.'))
      : null,
    ]),
    caption ? e('p', { className: 'wd-caption', key: 'c' }, caption) : null,
  ]);
};

export const SegToggle = ({ label, value, options, onPick, disabled }) =>
  e('div', { className: 'wd-sidetoggle' }, [
    label ? e('span', { className: 'wd-lab', key: 'l' }, label) : null,
    e(
      'div',
      { className: 'wd-seg', key: 's' },
      options.map((o) =>
        e(
          'button',
          {
            key: String(o.value),
            type: 'button',
            className: String(value) === String(o.value) ? 'is-on' : '',
            disabled: !!disabled,
            onClick: () => onPick(o.value),
          },
          o.label
        )
      )
    ),
  ]);

/**
 * A windowed row of pills with minus and plus-N paging, so a booking with many
 * dates never grows a scrollbar inside the card.
 */
export const PillRow = ({ items }) =>
  e('div', { className: 'wd-pillrow' },
    e('div', { className: 'wd-scroller' }, items));

// The one selection mark in the flow: a square that fills with a white check.
export const TickBox = ({ checked, size = 18 }) =>
  e(
    'span',
    {
      className: `wd-tick${checked ? ' is-on' : ''}`,
      style: { width: size, height: size },
      'aria-hidden': 'true',
    },
    e(
      'svg',
      {
        width: 12,
        height: 12,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: '#FFFFFF',
        strokeWidth: 3.2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      },
      e('path', { d: 'M20 6L9 17l-5-5' })
    )
  );

export const CheckRow = ({ checked, label, onToggle, disabled }) =>
  e(
    'button',
    {
      type: 'button',
      className: `wd-checkrow${checked ? ' is-on' : ''}`,
      disabled: !!disabled,
      onClick: onToggle,
    },
    [e(TickBox, { checked, key: 't' }), e('span', { key: 'l' }, label)]
  );
